<template>
	<v-card v-if="!(orderRows === null)">
		<v-card-title class="d-flex align-center">
			<v-icon
				start
				icon="$cashMultiple"
			/>
			<translate>Add Invoice</translate>
		</v-card-title>
		<v-card-text v-if="orderRows.length === 0">
			<v-row>
				<v-col>
					<translate>The order you wish to make an invoice for has no order rows</translate>
				</v-col>
			</v-row>
		</v-card-text>
		<div v-else>
			<v-card-text>
				<v-form ref="invoiceForm">
					<v-row>
						<v-col
							cols="12"
							sm="2"
						>
							<price-field
								v-model="fields.amount"
								:label="$gettext('Amount (euro and cent)')"
								phone-price="false"
								field-name="Invoice amount"
							/>
						</v-col>
						<v-col
							cols="12"
							sm="2"
						>
							<v-checkbox
								v-model="fields.refund"
								color="primary"
								:label="$gettext('For a refund')"
							/>
						</v-col>
						<v-col
							cols="12"
							sm="8"
						>
							<v-text-field
								v-model="fields.description"
								class="pt-2"
								variant="outlined"
								:rules="[v => !!v || $gettext('Description is required')]"
							>
								<template #label>
									<div>
										<translate>Description</translate> <small>(<translate>This is what the client sees on the invoice</translate>)</small>
									</div>
								</template>
							</v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-select
								v-model="fields.orderRowId"
								:label="$gettext('Order row')"
								:items="orderRows"
								:item-title="orderRowText"
								item-value="id"
								:disabled="orderRows.length === 1"
								:rules="[v => !!v || $gettext('Order row is required')]"
								variant="outlined"
							/>
						</v-col>
						<v-col>
							<v-select
								v-model="fields.invoiceRowTypeId"
								:label="$gettext('Invoice type')"
								:items="invoiceTypes"
								:item-title="invoiceTypeText"
								item-value="id"
								:rules="[v => !!v || $gettext('Invoice type is required')]"
								variant="outlined"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-textarea
								v-model="fields.note"
								variant="outlined"
							>
								<template #label>
									<div>
										<translate>Note</translate> <small>(<translate>Optional, only visible in the backoffice</translate>)</small>
									</div>
								</template>
							</v-textarea>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn
					color="primary"
					@click="submit"
				>
					<translate>Save</translate>
				</v-btn>
			</v-card-actions>
		</div>
	</v-card>
</template>

<script>
import {
	axios,
} from '../../services/axios-service';
import {
	displayError, inform,
} from 'laravel-front-library';

export default {
	name: 'CreateInvoiceCard',

	props: {
		orderId: {
			type: Number,
			required: true
		}
	},

	data () {
		return {
			fields: {
				amount: '',
				refund: false,
				description: '',
				invoiceRowTypeId: null,
				orderRowId: null,
				note: null
			},

			orderRows: [],
			typeNameBlacklist: [
				'Deposit',
				'Premium',
				'Deductable',
				'Premium non-collectable non-refundable IPT',
				'Premium non-collectable refundable IPT',
				'Deductable non-collectable',
				'Damage not covered',
			],
			showTax: localStorage.getItem('permissions').includes('taxtype.read'),
			invoiceTypes: [],
			disableButton: false
		};
	},

	mounted () {
		this.getInitialData();
	},

	methods: {
		/**
		 * Text for order row select
		 *
		 * @param {object} orderRow
		 * @returns {string}
		 */
		orderRowText: (orderRow) => orderRow.identifier + ' - ' + orderRow.currentModel,

		/**
		 * Text for invoice type select
		 *
		 * @param {object} invoiceType
		 * @returns {string}
		 */
		invoiceTypeText (invoiceType) {
			return invoiceType.name + (this.showTax ? ' - ' + invoiceType.taxtype.name : '');
		},

		/**
		 * Get data for the current order and order rows
		 *
		 * @returns {void}
		 */
		getInitialData () {
			axios.get('/api/v0/orderrows/withdevice', {
				params: {
					'order_id': this.orderId
				}
			}).then(({
				data: orderRows
			}) => {
				this.orderRows = orderRows;
				if (this.orderRows.length === 1) {
					this.fields.orderRowId = this.orderRows[this.orderRows.length - 1].id;
				}
			}).catch((e) => this.dataError(e));

			axios.get('/api/v0/invoicerowtypes').then(({
				data: types
			}) => {
				this.invoiceTypes = types.filter((type) => {
					return !(this.typeNameBlacklist.includes(type.name));
				});
			}).catch((e) => this.dataError(e));
		},

		/**
		 * Capture error from data retrieval and display message
		 *
		 * @param {object} error
		 * @returns {void}
		 */
		dataError (error) {
			displayError(this.$gettext('Something has gone wrong during data retrieval'));
			window.captureException(error);
		},

		/**
		 * Submit the new invoice
		 *
		 * @returns {void}
		 */
		submit () {
			if (!this.$refs.invoiceForm.validate()) {
				return;
			}

			const formData = new FormData();

			formData.append('orderId', this.orderId);
			for (const field in this.fields) {
				formData.append(field, this.fields[field]);
			}

			axios.post('/api/v0/invoices', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			}).then(({
				data: invoiceId
			}) => {
				inform(this.$gettext('Invoice added'));
				setTimeout(() => {
					window.location.href = `/bo/invoices/view?id=${invoiceId}`;
				}, 1000);
			}).catch((error) => {
				if (error.response.status === 400) {
					displayError(error.response.data);
				} else {
					displayError(this.$gettext('Failed to add invoice'));
				}
			});
		}
	}
};
</script>
